/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import CommentIcon from '@atlaskit/icon/core/comment';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { processExcerpt } from '../../utils/search-result-utils';

const styles = cssMap({
	container: {
		paddingTop: token('space.100'),
	},
	commentWrapper: {
		minWidth: '0px',
	},
	commentLink: {
		display: 'block',
		minWidth: '0px',
		textDecoration: 'none',
		color: token('color.text'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	commentCardRow: {
		alignItems: 'flex-start',
		minWidth: '0px',
	},
	commentIconColumn: {
		flexShrink: 0,
		paddingTop: token('space.025'),
	},
	commentContentColumn: {
		minWidth: '0px',
	},
	replyCard: {
		paddingInlineStart: token('space.200'),
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border.discovery'),
	},
	commentBodyWrapper: {
		minWidth: '0px',
	},
});

/**
 * Validates that a URL is safe to use as an href (https only).
 * Rejects javascript:, data:, and other non-https schemes.
 */
const isValidHttpsUrl = (url: string | null | undefined): url is string => {
	if (!url || typeof url !== 'string') {
		return false;
	}
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'https:';
	} catch {
		return false;
	}
};

export interface EmbeddedComment {
	id?: string;
	/**
	 * The ID of the parent entity — either the document ID (for top-level comments)
	 * or another comment ID (for threaded replies). Used to sort top-level comments
	 * before replies in the embedded comments list.
	 */
	parentId?: string | null;
	excerpt: string | null;
	url?: string | null;
	authorName?: string | null;
}

export interface EmbeddedCommentsProps {
	comments: EmbeddedComment[];
	maxComments?: number;
	/**
	 * When adding expand/collapse for "X more comments", use FormattedMessage for i18n.
	 * Example: <FormattedMessage id="embedded-comments.more" defaultMessage="{count} more comments" values={{ count }} />
	 */
}

/**
 * Renders embedded comments that matched a search query within a document result.
 * Shows author name followed by comment excerpt with highlighted matches.
 * Comments are clickable links when URL is available.
 */
export const EmbeddedComments = ({
	comments,
	maxComments = 3,
}: EmbeddedCommentsProps): React.JSX.Element | null => {
	if (!comments.length) {
		return null;
	}

	// Order top-level comments before threaded replies using parentId.
	// A comment is a reply if its parentId matches another comment's id in the list.
	// This ensures the most contextual comments surface first within the flat list.
	const commentIds = new Set(comments.map((c) => c.id).filter(Boolean));
	const sortedComments = [...comments].sort((a, b) => {
		const aIsReply = a.parentId != null && commentIds.has(a.parentId);
		const bIsReply = b.parentId != null && commentIds.has(b.parentId);
		if (aIsReply === bIsReply) {
			return 0;
		}
		return aIsReply ? 1 : -1;
	});
	const visibleComments = sortedComments.slice(0, maxComments);

	const commentContent = (comment: (typeof visibleComments)[0]) => {
		// noLeadingEllipsis=true prevents processExcerpt from prepending '...' to long excerpts.
		// Leading ellipsis is wrong in a card layout — truncation only happens visually at the end.
		const processedExcerpt = comment.excerpt ? processExcerpt(comment.excerpt, true) : null;
		const isReply = comment.parentId != null && commentIds.has(comment.parentId);

		return (
			<Box xcss={isReply ? styles.replyCard : styles.commentWrapper}>
				<Inline space="space.100" xcss={styles.commentCardRow}>
					<Box xcss={styles.commentIconColumn}>
						<CommentIcon label="Comment" color="currentColor" />
					</Box>
					<Stack space="space.025" xcss={styles.commentContentColumn}>
						{comment.authorName ? (
							<Text color="color.text.subtle" weight="medium">
								{comment.authorName}
							</Text>
						) : null}
						{processedExcerpt ? (
							<Tooltip content={processedExcerpt}>
								{(tooltipProps) => (
									<Box {...tooltipProps} xcss={styles.commentBodyWrapper}>
										<Text maxLines={2} color="color.text">
											{processedExcerpt}
										</Text>
									</Box>
								)}
							</Tooltip>
						) : null}
					</Stack>
				</Inline>
			</Box>
		);
	};

	const commentWrapper = (comment: (typeof visibleComments)[0], index: number) => {
		const key = comment.id || index;
		if (isValidHttpsUrl(comment.url)) {
			return (
				<Anchor
					key={key}
					href={comment.url}
					xcss={styles.commentLink}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
				>
					{commentContent(comment)}
				</Anchor>
			);
		}

		return (
			<Box key={key} xcss={styles.commentLink}>
				{commentContent(comment)}
			</Box>
		);
	};

	return (
		<Box xcss={styles.container}>
			<Stack space="space.050">{visibleComments.map(commentWrapper)}</Stack>
		</Box>
	);
};
